import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { OsCommonContext, SelectedHierarchyItem } from '@wppopen/core';

import { environment } from '../../../environments/environment';
import { CryptoUtils } from '../../shared/utils/crypto.utils';

declare global {
	interface Window {
		dataLayer: any[];
		wppopen_dataLayer: any[];
		heap: any[];
		heapReadyCb: any[];
	}
}

export interface CCEUserContext {
	cce_user_id: string;
}

export interface WppOpenUserContext {
	user_id: string;
	user_company?: string;
	user_country_code?: string;
	user_department?: string;
	user_job_role?: string;
	workspace_market?: string;
	workspace_industry?: string;
	workspace_brand?: string;
	workspace_client?: string;
	workspace_region?: string;
	workspace_custom?: string;
	project?: string;
	context_type: string;
	tenant_name: string;
	application_id?: string;
	application_instance_id?: string;
	product_type?: string;
	nocode_apptype?: string;
}

@Injectable({
	providedIn: 'root'
})
export class WppOpenAnalyticsService {
	private readonly renderer: Renderer2;
	private readonly wppOpenGtmId = environment.wppOpenGtmId || '';
	private readonly gtmId = environment.gtmId || '';
	private readonly heapAppId = environment.wppOpenHeapAppId || '';
	private readonly debugMode = `${environment.analyticsDebugMode}` === 'true';
	private readonly productContext = {
		product_name: 'CCE',
		product_category: 'Commerce Studio',
		product_subcategory: 'Content Creation'
	};

	private cceContext: CCEUserContext | null = null;
	private wppOpenContext: WppOpenUserContext | null = null;
	private previousUrl = '';
	private initialized = false;
	private routeTrackingSetup = false;
	private scriptsBootstrapped = false;
	private heapInitialized = false;
	private gtmReady = false;
	private expectedGtmScripts = 0;
	private gtmScriptsLoaded = 0;
	private eventQueue: any[] = [];

	constructor(private readonly router: Router, rendererFactory: RendererFactory2) {
		this.renderer = rendererFactory.createRenderer(null, null);
		this.previousUrl = window.location.pathname;
	}

	public async createWppOpenUserContext(context: OsCommonContext): Promise<WppOpenUserContext> {
		const user = (context.userDetails || {}) as OsCommonContext['userDetails'] & {
			company?: string;
			geoRegion?: string;
		};
		const wppOpenContext: WppOpenUserContext = {
			context_type: 'os',
			tenant_name: context.tenant?.name || 'unknown',
			user_id: (await CryptoUtils.hashEmail(user.email || '')) || 'unknown',
			user_company: user.agency || user.company || '',
			user_country_code: user.countryAlpha2Code || '',
			user_department: user.department || '',
			user_job_role: user.jobTitle || '',
			project: context.project?.name,
			application_id: context.appInstance?.devhubAppId || '',
			application_instance_id: context.appInstance?.id || '',
			product_type: context.appInstance?.devhubMetadata?.productType || '',
			nocode_apptype: context.appInstance?.devhubMetadata?.noCodeAppType || ''
		};

		const hierarchy = context.hierarchy;
		if (!hierarchy?.mapping) {
			return wppOpenContext;
		}

		const workspaceHierarchy: Record<string, SelectedHierarchyItem> = {};
		let currentAzId = hierarchy.azId;

		while (currentAzId && hierarchy.mapping[currentAzId]) {
			const workspace = hierarchy.mapping[currentAzId];
			workspaceHierarchy[workspace.type] = workspace;
			currentAzId = workspace.parentAzId;
		}

		return {
			...wppOpenContext,
			workspace_market: workspaceHierarchy['MARKET']?.name || undefined,
			workspace_client: workspaceHierarchy['CLIENT']?.name || undefined,
			workspace_industry: workspaceHierarchy['INDUSTRY']?.name || undefined,
			workspace_brand: workspaceHierarchy['BRAND']?.name || undefined,
			workspace_region: workspaceHierarchy['REGION']?.name || undefined,
			workspace_custom: workspaceHierarchy['CUSTOM']?.name || undefined
		};
	}

	public async initializeAnalytics(cceContext: CCEUserContext, wppOpenContext?: WppOpenUserContext): Promise<void> {
		if (this.initialized || !cceContext || !this.hasActiveTransport(cceContext, wppOpenContext)) {
			return;
		}

		this.cceContext = cceContext;
		this.wppOpenContext = wppOpenContext || null;
		this.bootstrapScripts();
		if (this.hasWppHeap()) {
			this.initHeap(this.heapAppId);
		}

		if (!this.routeTrackingSetup) {
			this.setupRouteTracking();
			this.routeTrackingSetup = true;
		}

		this.trackAppLaunch();
		this.initialized = true;
		this.trackInitialPageView();
	}

	public isInitialized(): boolean {
		return this.initialized;
	}

	public trackPageView(pageUrl: string = this.router.url || window.location.pathname, pageTitle: string = document.title): void {
		if (!this.initialized) {
			return;
		}

		this.pushToDataLayers(
			this.buildEventPayload('page_view', {
				pageUrl,
				pageTitle
			})
		);
		this.previousUrl = pageUrl;
	}

	public trackButtonClick(buttonText: string, targetPath?: string): void {
		this.trackEvent('click', {
			click_object: 'button',
			click_text: buttonText,
			click_page_path: this.router.url,
			click_target_path: targetPath
		});
	}

	public trackFeatureEvent(eventName: string, category: string, context: string): void {
		this.trackEvent(eventName, {
			feature_category: category,
			feature_context: context
		});
	}

	public trackEvent(eventName: string, data: object): void {
		this.pushToDataLayers(this.buildEventPayload(eventName, data));
	}

	private bootstrapScripts(): void {
		if (this.scriptsBootstrapped) {
			return;
		}

		this.scriptsBootstrapped = true;
		this.expectedGtmScripts = [this.hasInternalTransport(), this.hasWppTransport()].filter(Boolean).length;
		this.gtmReady = this.expectedGtmScripts === 0;

		if (this.hasInternalTransport()) {
			this.initDataLayer(this.gtmId, 'dataLayer');
		}

		if (this.hasWppTransport()) {
			this.initDataLayer(this.wppOpenGtmId, 'wppopen_dataLayer');
		}
	}

	private initDataLayer(gtmId: string, layerName: 'dataLayer' | 'wppopen_dataLayer'): void {
		const win = window as any;
		win[layerName] = win[layerName] || [];
		win[layerName].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

		const existingScript = document.getElementById(`wpp-open-analytics-${layerName}`);
		if (existingScript) {
			this.gtmScriptsLoaded++;
			this.markGtmReadyIfNeeded();
			return;
		}

		const script = this.renderer.createElement('script');
		script.id = `wpp-open-analytics-${layerName}`;
		script.async = true;
		script.type = 'text/javascript';
		script.setAttribute('data-wpp-open-analytics', layerName);
		const dl = layerName !== 'dataLayer' ? `&l=${layerName}` : '';
		script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${dl}`;
		script.onload = () => {
			this.gtmScriptsLoaded++;
			this.markGtmReadyIfNeeded();
		};

		this.renderer.appendChild(document.head, script);
	}

	private markGtmReadyIfNeeded(): void {
		if (this.gtmScriptsLoaded < this.expectedGtmScripts) {
			return;
		}

		this.gtmReady = true;
		this.flushEventQueue();
	}

	private initHeap(heapAppId: string): void {
		if (this.heapInitialized || document.getElementById('wpp-open-heap-analytics')) {
			this.heapInitialized = true;
			return;
		}

		const script = this.renderer.createElement('script');
		script.id = 'wpp-open-heap-analytics';
		script.type = 'text/javascript';
		script.setAttribute('data-wpp-open-analytics', 'heap');
		script.text = `
			window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.eu.heap-api.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(i){return function(){heap.push([i].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
			heap.load("${heapAppId}");
		`;

		this.renderer.appendChild(document.head, script);
		this.heapInitialized = true;
	}

	private trackAppLaunch(): void {
		this.pushToDataLayers(this.buildEventPayload('app_launch'));
	}

	private trackInitialPageView(): void {
		if (!this.initialized && this.debugMode) {
			console.warn('[WPP Open Analytics] page_view skipped before init');
		}

		this.trackPageView();
	}

	private trackVirtualPageView(pageUrl: string, pageTitle: string): void {
		if (!this.initialized) {
			return;
		}

		this.pushToDataLayers(
			this.buildEventPayload('virtual_page_view', {
				pageUrl,
				pageTitle,
				previousUrl: this.previousUrl
			})
		);

		this.previousUrl = pageUrl;
	}

	private setupRouteTracking(): void {
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			this.trackVirtualPageView(event.urlAfterRedirects, document.title);
		});
	}

	private pushToDataLayers(data: any): void {
		if (!this.hasActiveTransport() || !this.gtmReady) {
			this.eventQueue.push(data);
			return;
		}

		if (this.hasInternalTransport()) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push(data);
		}

		if (this.hasWppTransport()) {
			window.wppopen_dataLayer = window.wppopen_dataLayer || [];
			window.wppopen_dataLayer.push(data);
		}
	}

	private flushEventQueue(): void {
		while (this.eventQueue.length > 0) {
			const event = this.eventQueue.shift();
			if (event) {
				this.pushToDataLayers(event);
			}
		}
	}

	private buildEventPayload(eventName: string, data: object = {}): Record<string, unknown> {
		return {
			event: eventName,
			...this.productContext,
			...(this.cceContext || {}),
			...(this.wppOpenContext || {}),
			...data
		};
	}

	private hasInternalTransport(): boolean {
		return Boolean(this.gtmId);
	}

	private hasWppTransport(): boolean {
		return Boolean(this.wppOpenContext && this.wppOpenGtmId);
	}

	private hasWppHeap(): boolean {
		return Boolean(this.wppOpenContext && this.heapAppId);
	}

	private hasActiveTransport(
		cceContext: CCEUserContext | null = this.cceContext,
		wppOpenContext: WppOpenUserContext | null = this.wppOpenContext
	): boolean {
		return Boolean(cceContext && this.gtmId) || Boolean(wppOpenContext && this.wppOpenGtmId);
	}
}
